import { c as _c } from "react/compiler-runtime";
import { Typography, type SxProps } from '@mui/material';
import { Provider, type ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { hasValidConditionValues } from '@/types/advanceFilter';
import type { BackupPolicyEditState } from './BackupPolicyEditState';
import { isPitrPolicyType } from './backupPoliciesTypes';
import { useBackupPolicySupportedProperties } from './backupPolicyProperties';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';
const useSourceProviders = (policyType: BackupPolicyType): Provider[] | undefined => {
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD) {
    return [Provider.Aws];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    return [Provider.Azure];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY) {
    return [Provider.Aws, Provider.Gcp, Provider.MongoAtlas];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR) {
    return [Provider.Azure];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR) {
    return [Provider.Aws];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR) {
    return [Provider.Gcp];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR) {
    return [Provider.Azure];
  }
  return undefined;
};
export const BackupPolicyConditionEditor = props => {
  const $ = _c(49);
  const nextButtonDisabledTooltip = props.wizardState.resourcesScopeType === "useConditions" && !hasValidConditionValues(props.wizardState.condition) ? "Please create a condition with all required fields" : "Please select at least one resource type";
  const supportedResourceTypes = useBackupPolicyAvailableTypes()[props.wizardState.policyType];
  const sourceProviders = useSourceProviders(props.wizardState.policyType);
  const supportedProperties = useBackupPolicySupportedProperties(props.wizardState.resourceTypes ?? supportedResourceTypes, sourceProviders, props.wizardState.policyType);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      padding: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.wizardState.condition || $[2] !== props.wizardState.resourcesScopeType) {
    t1 = () => props.wizardState.resourcesScopeType !== "useConditions" || hasValidConditionValues(props.wizardState.condition);
    $[1] = props.wizardState.condition;
    $[2] = props.wizardState.resourcesScopeType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props) {
    t2 = async () => {
      props.setNextStep(props.currentStep.next?.[0]);
    };
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props) {
    t3 = name => props.setWizardState(state => ({
      ...state,
      name
    }));
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props.wizardState.focusOnName || $[9] !== props.wizardState.name || $[10] !== t3) {
    t4 = <RenameEntityName focusOnMount={props.wizardState.focusOnName} label="Backup policy name" name={props.wizardState.name} onSubmit={t3} />;
    $[8] = props.wizardState.focusOnName;
    $[9] = props.wizardState.name;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props.wizardState.policyType) {
    t5 = <BackupPolicySupportedDisclaimer policyType={props.wizardState.policyType} />;
    $[12] = props.wizardState.policyType;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = props.wizardState.resourceTypes ?? supportedResourceTypes;
  let t7;
  if ($[14] !== props) {
    t7 = condition => props.setWizardState({
      ...props.wizardState,
      condition
    });
    $[14] = props;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  let t9;
  if ($[16] !== props) {
    t8 = includedResources => props.setWizardState({
      ...props.wizardState,
      include: includedResources
    });
    t9 = excludedResources => props.setWizardState({
      ...props.wizardState,
      exclude: excludedResources
    });
    $[16] = props;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[19] !== props) {
    t10 = resourcesScopeType => props.setWizardState({
      ...props.wizardState,
      resourcesScopeType
    });
    $[19] = props;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      mb: "24px"
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== props.wizardState.policyType || $[23] !== props.wizardState.resourceTypes) {
    t12 = <BackupPolicyConditionTitle resourceTypes={props.wizardState.resourceTypes} policyType={props.wizardState.policyType} sx={t11} />;
    $[22] = props.wizardState.policyType;
    $[23] = props.wizardState.resourceTypes;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too.";
  let t14;
  if ($[25] !== props.wizardState.condition || $[26] !== props.wizardState.exclude || $[27] !== props.wizardState.include || $[28] !== props.wizardState.policyType || $[29] !== props.wizardState.resourceTypes || $[30] !== props.wizardState.resourcesScopeType || $[31] !== sourceProviders || $[32] !== supportedProperties || $[33] !== t10 || $[34] !== t12 || $[35] !== t13 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t14 = <ResourcesSelectionEditor policyType={props.wizardState.policyType} selectedResourceTypes={props.wizardState.resourceTypes} supportedProviders={sourceProviders} supportedResourceTypes={t6} condition={props.wizardState.condition} setCondition={t7} includedResources={props.wizardState.include} excludedResources={props.wizardState.exclude} setIncludedResources={t8} setExcludedResources={t9} resourcesScopeType={props.wizardState.resourcesScopeType} setResourcesScopeType={t10} supportedProperties={supportedProperties} conditionTitle={t12} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={t13} manualOverridesDescription="Manually include or exclude resources from your backup policy, regardless of dynamic selection." />;
    $[25] = props.wizardState.condition;
    $[26] = props.wizardState.exclude;
    $[27] = props.wizardState.include;
    $[28] = props.wizardState.policyType;
    $[29] = props.wizardState.resourceTypes;
    $[30] = props.wizardState.resourcesScopeType;
    $[31] = sourceProviders;
    $[32] = supportedProperties;
    $[33] = t10;
    $[34] = t12;
    $[35] = t13;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== nextButtonDisabledTooltip || $[42] !== props.stepperLabels || $[43] !== t1 || $[44] !== t14 || $[45] !== t2 || $[46] !== t4 || $[47] !== t5) {
    t15 = <><StepContainer sx={t0} stepperLabels={props.stepperLabels} stepperIndex={0} nextButtonDisabledTooltip={nextButtonDisabledTooltip} canGoNext={t1} onNextClick={t2} navigationComponent={t4}>{t5}{t14}</StepContainer></>;
    $[41] = nextButtonDisabledTooltip;
    $[42] = props.stepperLabels;
    $[43] = t1;
    $[44] = t14;
    $[45] = t2;
    $[46] = t4;
    $[47] = t5;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
};
export const BackupPolicyConditionTitle = t0 => {
  const $ = _c(7);
  const {
    resourceTypes,
    policyType,
    sx
  } = t0;
  if (!isPitrPolicyType(policyType) && policyType !== BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    let t1;
    if ($[0] !== sx) {
      t1 = <Typography variant="subtitle1" sx={sx}>Include resources based on these conditions:</Typography>;
      $[0] = sx;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== resourceTypes) {
    t1 = resourceTypes && <span className="font-medium">{resourceTypes?.map(_temp).join(", ")}{" "}</span>;
    $[2] = resourceTypes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== sx || $[5] !== t1) {
    t2 = <Typography variant="subtitle1" sx={sx}>Include{" "}{t1}resources based on these conditions:</Typography>;
    $[4] = sx;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(x) {
  return ResourceTypes[x].displayName;
}